import { defineStore } from 'pinia';
import { useApi } from '@lib/api.ts';
import { useGlobalStore } from '@/store/global-store-base.ts';
import {
  CustomFieldEntityType,
  type CustomFieldGroupDto,
  type CustomFieldValueDto,
  isTextCustomValueDto,
  isMultiSelectCustomValueDto,
} from '@/ApiClient.ts';
import { useCustomFields } from '@/store/custom-fields.ts';
import { useHierarchy } from '@/store/hierarchy.ts';
import { computed } from 'vue';

export type CustomFieldValues = Record<number, CustomFieldValueDto>;

export const useCustomFieldGroups = defineStore('custom-field-groups', () => {
  const api = useApi();
  const globalStoreBase = useGlobalStore({
    load: api.customFieldGroup_ListGroups,
    dto: 'CustomFieldGroupDto',
    //create: api.customFieldGroup_CreateGroup,
    signalR: 'CustomFieldGroupUpdate',
  });
  const { loaded } = globalStoreBase;

  function forEntity(entity: CustomFieldEntityType) {
    return loaded.value.filter((group) => getFields(group).some((f) => f.entities.includes(entity)));
  }

  // Groups that are not restricted to any hierarchy node, i.e. visible everywhere.
  const groupsAvailableOnAllNodes = computed(() => {
    const restrictedGroupIds = useHierarchy().fullNodes.flatMap((node) => node.customFieldGroupIds);
    return loaded.value.filter((group) => !restrictedGroupIds.includes(group.id));
  });

  // A group with no node restrictions is visible everywhere; otherwise it is visible only for the
  // listed nodes and their subnodes. Mirrors useTags().isVisible.
  function isVisible(group: CustomFieldGroupDto, hierarchyNodeId?: number) {
    if (group.hierarchyNodeIds?.length === 0) return true;
    if (!hierarchyNodeId) return false;

    const visibleGroupIds = useHierarchy()
      .getNodeAndAncestors(hierarchyNodeId)
      .flatMap((node) => node.customFieldGroupIds);

    return visibleGroupIds.includes(group.id);
  }

  function visibleForEntity(entity: CustomFieldEntityType, hierarchyNodeId?: number) {
    return forEntity(entity).filter((group) => isVisible(group, hierarchyNodeId));
  }

  function getFields(group: CustomFieldGroupDto) {
    return useCustomFields().loaded.filter((field) => field.groupId == group.id);
  }

  function hasAnyValue(group: CustomFieldGroupDto, values: CustomFieldValues) {
    return getFields(group).some((field) => {
      const value = values[field.id];
      if (value === undefined) return false;
      if (isTextCustomValueDto(value)) return !!value.value;
      if (isMultiSelectCustomValueDto(value)) return value.optionIds?.length > 0;
      return true;
    });
  }

  function getGroupByFieldId(fieldId: number): CustomFieldGroupDto | undefined {
    const field = useCustomFields().loaded.find((f) => f.id === fieldId);
    if (!field || !field.groupId) return undefined;
    return loaded.value.find((g) => g.id === field.groupId);
  }

  return {
    ...globalStoreBase,
    forEntity,
    groupsAvailableOnAllNodes,
    isVisible,
    visibleForEntity,
    getFields,
    hasAnyValue,
    getGroupByFieldId,
  };
});
